import {
	Component,
	ViewChild,
	Input,
	Output,
	EventEmitter,
	AfterViewInit,
} from '@angular/core';
import {
	JobShift,
	BulkAddShiftsRequest,
	BulkDeleteShiftsRequest,
	JobsClient,
	DateRange,
	JobShiftListArgs,
	Job
} from '../../../../shared/api';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { animateList } from '../../../../shared/animations';
import * as moment from 'moment';
import {
	tap,
	switchMap,
	filter,
	exhaustMap,
	mapTo,
	takeUntil,
	debounceTime,
} from 'rxjs/operators';
import {
	AddEditShiftDialogComponent,
	ShiftDialogData,
} from '../../shared/add-edit-shift-dialog/add-edit-shift-dialog.component';
import { Store } from '@ngrx/store';
import { JobDetailResetJobShifts } from 'src/admin-app/jobs/+state/job-shift/job-shift.actions';
import { Subject, BehaviorSubject } from 'rxjs';
import { isNull } from 'lodash';
import { FilterCollection } from 'src/admin-app/utilities/list-filtering/shared';
import {
	withMatPaginator,
	withMatSort,
} from 'src/admin-app/utilities/list-filtering';
import { SelfDestruct } from '../../../../shared/utilities/self-destruct';
import { withSubject } from 'src/admin-app/utilities/list-filtering/with-subject';
import {
	JobShiftState,
	jobShiftsFeatureKey,
} from 'src/admin-app/jobs/+state/job-shift/job-shift.reducer';
import { displayDescription } from 'src/shared/utilities/utility-functions';

@Component({
	selector: 'rpfsa-shift-list',
	templateUrl: './shift-list.component.html',
	styleUrls: ['./shift-list.component.scss'],
	animations: [animateList],
})
export class ShiftListComponent extends SelfDestruct implements AfterViewInit {
	@ViewChild(MatSort, { static: true })
	sort: MatSort;
	@ViewChild(MatPaginator, { static: true })
	paginator: MatPaginator;

	private _job: Job;
	private _refresh = new Subject<{}>();
	private _jobId = new BehaviorSubject<{ jobId?: number }>(null);
	private _showServiceType: boolean;
	@Input()
	set job(jobItem: Job) {
		if (jobItem && jobItem.id) {
			this._jobId.next({ jobId: jobItem.id });
			this._job = jobItem;
		} else {
			this._jobId.next(null);
		}
	}

	@Input()
	set showServiceType(value: boolean) {
		this._showServiceType = value;
		if (this._showServiceType) {
			this.resultColumns.splice(3, 0, 'serviceType');
		}
	}

	@Output() outRangedShift = new EventEmitter<DateRange>();
	@Output() loading = new EventEmitter();

	readonly displayDescription = displayDescription;
	emptyColumns = ['empty'];
	resultColumns = ['rwic', 'day', 'startTime', 'deleteShift'];
	resultCount = 0;

	dataSource: JobShift[] = [];

	constructor(
		private shiftsDialog: MatDialog,
		private jobsClient: JobsClient,
		private store: Store<{ [jobShiftsFeatureKey]: JobShiftState }>
	) {
		super();
	}
	ngAfterViewInit() {
		const filters = new FilterCollection<JobShiftListArgs>(
			withSubject(this._refresh),
			withSubject(this._jobId),
			withMatPaginator(this.paginator),
			withMatSort(this.sort)
		);

		filters.valueChanges
			.pipe(
				filter((v) => !!v.jobId),
				debounceTime(200),
				takeUntil(this.destroyed),
				tap(() => this.loading.emit(true)),
				switchMap((args) => this.jobsClient.getShifts(args)),
				tap(() => this.loading.emit(false))
			)
			.subscribe(
				(pagedResults) => {
					this.dataSource = pagedResults.results;
					this.paginator.pageIndex = pagedResults.pageNumber;
					this.resultCount = pagedResults.resultCount;
				},
				(error: any) => {
					this.loading.emit(false);
					throw new Error(error);
				}
			);

		filters.setValue({
			jobId: this._jobId.value?.jobId,
			pageSize: 10,
			pageNumber: 0,
			sortCriteria: [{ sortKey: 'day', isDescending: false }],
		});
	}

	addShift(): void {
		this.shiftsDialog
			.open(AddEditShiftDialogComponent, {
				minWidth: '20rem',
				data: {
					add: false,
					addSingle: true,
					delete: false,
					job: this._job,
					showServiceType: this._showServiceType,
				} as ShiftDialogData,
			})
			.afterClosed()
			.pipe(
				filter(Boolean),
				exhaustMap((shift: JobShift) =>
					this.jobsClient.addShift(shift).pipe(mapTo(shift))
				)
			)
			.subscribe((shift) => {
				this._refresh.next();
				const day = moment.utc(shift.day);
				if (day.isBefore(this._job.start)) {
					this.outRangedShift.emit({
						begin: shift.day,
						end: this._job.end,
					});
				}
				if (day.isAfter(this._job.end)) {
					this.outRangedShift.emit({
						begin: this._job.start,
						end: shift.day,
					});
				}
			});
	}

	addShifts(): void {
		this.shiftsDialog
			.open(AddEditShiftDialogComponent, {
				minWidth: '20rem',
				data: {
					add: true,
					addSingle: false,
					delete: false,
					job: this._job,
					showServiceType: this._showServiceType,
				} as ShiftDialogData,
			})
			.afterClosed()
			.pipe(
				filter(Boolean),
				exhaustMap((shifts: BulkAddShiftsRequest) =>
					this.jobsClient.bulkAddShifts(shifts).pipe(mapTo(shifts))
				)
			)
			.subscribe((request) => {
				const startDay = moment.utc(request.startDate);
				const endDate = moment.utc(request.endDate);
				this._refresh.next();
				if (startDay.isBefore(this._job.start)) {
					this.outRangedShift.emit({
						begin: request.startDate,
						end: this._job.end,
					});
				}
				if (endDate.isAfter(this._job.end)) {
					this.outRangedShift.emit({
						begin: this._job.start,
						end: request.endDate,
					});
				}
			});
	}

	deleteShifts(): void {
		this.shiftsDialog
			.open(AddEditShiftDialogComponent, {
				minWidth: '20rem',
				data: {
					add: false,
					addSingle: false,
					delete: true,
					job: this._job,
					showServiceType: this._showServiceType,
				} as ShiftDialogData,
			})
			.afterClosed()
			.pipe(
				filter(Boolean),
				exhaustMap((deleteRequest: BulkDeleteShiftsRequest) =>
					this.jobsClient
						.bulkDeleteFutureShifts(deleteRequest)
						.pipe(mapTo(deleteRequest))
				)
			)
			.subscribe(() => {
				this._refresh.next();
			});
	}

	deleteShift(shift: JobShift): void {
		this.jobsClient
			.deleteJobShift(shift.id)
			.pipe(
				tap(() => {
					this.store.dispatch(
						new JobDetailResetJobShifts({
							jobId: this._job.id,
							startDate: moment.utc(shift.day),
						})
					);
				})
			)
			.subscribe(() => {
				this._refresh.next();
			});
	}

	getStartTime(shift: JobShift): string {
		return shift.startTime
			? moment.utc(shift.startTime, 'HH:mm:ss').format('HH:mm')
			: '';
	}

	isRwicNull(shift: JobShift): Boolean {
		return isNull(shift.rwic);
	}
}
